import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { hasConsentChoice, setConsent } from "@/lib/analytics";
import { useLanguage } from "@/context/LanguageContext";
import { Cookie, X } from "lucide-react";

/**
 * KVKK / GDPR uyumlu çerez izin banner'ı.
 * - İlk ziyarette gösterilir; kullanıcının seçimi localStorage'a kaydedilir.
 * - "Kabul Et" → Google Consent Mode v2 update + Meta Pixel init.
 * - "Reddet"  → GA cookieless modda pinger, Meta Pixel init edilmez.
 * - Alt köşede küçük "Çerez Ayarları" bağlantısı ile sonradan değiştirilebilir.
 */
export default function CookieConsentBanner() {
  const { locale } = useLanguage();
  const isEn = locale === "en";
  const [visible, setVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    // Ziyaretçinin daha önce karar verip vermediğini kontrol et.
    if (!hasConsentChoice()) {
      // Küçük bir gecikmeyle göster ki sayfa yüklendikten sonra dikkat çeksin.
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    // Footer'daki "Çerez Ayarları" linki tekrar açmak istediğinde
    // window'daki custom event üzerinden banner'ı yeniden görünür yap.
    const handler = () => setVisible(true);
    window.addEventListener("kahu:open-cookie-consent", handler);
    return () => window.removeEventListener("kahu:open-cookie-consent", handler);
  }, []);

  // Bir modal (bağış akışı vb.) açıkken banner alttaki butonların üstüne biniyor →
  // modal kapanana kadar banner'ı gizle.
  useEffect(() => {
    const check = () => setDialogOpen(!!document.querySelector('[data-radix-dialog-content],[role="dialog"][data-state="open"]'));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state"] });
    return () => obs.disconnect();
  }, []);

  // Banner görünürken sayfanın alt kısmındaki butonları kapatmasın →
  // body'ye banner yüksekliği kadar alt boşluk ekle.
  useEffect(() => {
    if (!visible || dialogOpen) {
      document.body.style.paddingBottom = "";
      return;
    }
    const h = cardRef.current?.getBoundingClientRect().height || 0;
    document.body.style.paddingBottom = `${Math.round(h) + 24}px`;
    return () => {
      document.body.style.paddingBottom = "";
    };
  }, [visible, dialogOpen]);

  const accept = () => {
    setConsent("granted");
    setVisible(false);
  };

  const reject = () => {
    setConsent("denied");
    setVisible(false);
  };

  if (!visible || dialogOpen) return null;

  const T = {
    title: isEn ? "This site uses cookies" : "Bu site çerez kullanır",
    body: isEn
      ? "We use cookies to understand how visitors reach and use KAHU so we can bring more animals to safe homes. You can accept, reject, or read our details."
      : "KAHU'ya nasıl geldiğinizi ve nasıl kullandığınızı anlamak için çerez kullanıyoruz — böylece daha fazla cana yuva bulabiliyoruz. Kabul edebilir, reddedebilir veya detayları okuyabilirsiniz.",
    accept: isEn ? "Accept all" : "Tümünü kabul et",
    reject: isEn ? "Reject non-essential" : "Zorunlu olmayanları reddet",
    details: isEn ? "Privacy details" : "Gizlilik detayları",
    close: isEn ? "Close" : "Kapat",
  };

  const privacyHref = isEn ? "/en/gizlilik" : "/gizlilik";

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 inset-x-0 z-[9999] p-3 sm:p-5 pointer-events-none"
      role="dialog"
      aria-live="polite"
      aria-label={T.title}
    >
      <div ref={cardRef} className="pointer-events-auto max-w-3xl mx-auto rounded-2xl shadow-2xl border border-border/60 bg-background/95 backdrop-blur-md p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="hidden sm:flex h-10 w-10 rounded-full bg-primary/10 items-center justify-center flex-shrink-0">
            <Cookie className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h2 className="text-base font-semibold text-foreground">{T.title}</h2>
              <button
                type="button"
                onClick={reject}
                aria-label={T.close}
                className="text-muted-foreground hover:text-foreground transition-colors -mt-1"
                data-testid="cookie-consent-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              {T.body}{" "}
              <a
                href={privacyHref}
                className="underline underline-offset-2 hover:text-foreground"
                data-testid="cookie-consent-details-link"
              >
                {T.details}
              </a>
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={reject}
                data-testid="cookie-consent-reject-btn"
                className="sm:min-w-[180px]"
              >
                {T.reject}
              </Button>
              <Button
                size="sm"
                onClick={accept}
                data-testid="cookie-consent-accept-btn"
                className="sm:min-w-[160px] shadow-lg shadow-primary/20"
              >
                {T.accept}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
